import lodashDebounce from 'lodash/debounce'
import { useEffect, useMemo, useState } from 'react'

import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'

import type { ClientLibrary, ClientLibraryId } from 'types/client-library'

import { useDispatch } from 'hooks/use-redux'

type BooleanLibraryField = {
  [K in keyof ClientLibrary]: ClientLibrary[K] extends boolean ? K : never
}[keyof ClientLibrary]

const DEBOUNCE_MS = 500

/**
 * Optimistically toggles a boolean field on a client library. The switch
 * flips immediately on every click; the network request is debounced so
 * only the last value of a rapid burst is sent. Reverts locally if that
 * request fails (clientLibraryUpdate already shows an error toast).
 */
export function useDebouncedLibraryToggle(
  libraryId: ClientLibraryId,
  field: BooleanLibraryField,
  currentValue: boolean,
): [boolean, (next: boolean) => void] {
  const dispatch = useDispatch()
  const [value, setValue] = useState(currentValue)

  useEffect(() => {
    setValue(currentValue)
  }, [currentValue])

  const debouncedUpdate = useMemo(
    () =>
      lodashDebounce(async (next: boolean) => {
        try {
          await dispatch(
            clientLibraryUpdate(
              libraryId,
              { [field]: next } as Partial<ClientLibrary>,
              { rethrowError: true },
            ),
          )
        } catch {
          setValue(!next)
        }
      }, DEBOUNCE_MS),
    [dispatch, libraryId, field],
  )
  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate])

  const toggle = (next: boolean) => {
    setValue(next)
    debouncedUpdate(next)
  }

  return [value, toggle]
}
